import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, Users, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function Billing({ slug }) {
  const [usage, setUsage] = useState(null);
  const [paying, setPaying] = useState(false);
  const load = () => api.get(`/events/${slug}/usage`).then((r) => setUsage(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const pay = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/events/${slug}/pay`);
      if (r.data.nothing_due) { toast.info(r.data.message || "No outstanding dues"); load(); return; }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); return; }
      const order = r.data;
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Arenafy",
        description: `Arena capacity · ${order.invoice_no}`,
        prefill: order.prefill,
        theme: { color: "#6d28d9" },
        handler: async (resp) => {
          try {
            await api.post(`/events/${slug}/pay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            confetti({ particleCount: 120, spread: 70 });
            toast.success(`Payment successful · ${order.invoice_no}`);
            load();
          } catch (e) { toast.error(e.response?.data?.detail || "Payment verification failed"); }
        },
        modal: { ondismiss: () => { toast.info("Payment cancelled"); setPaying(false); } },
      });
      rzp.on("payment.failed", (resp) => { toast.error(resp.error?.description || "Payment failed"); });
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); } finally { setPaying(false); }
  };
  if (!usage) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  const S = usage.symbol;
  if (usage.billing_enabled === false) {
    return (
      <div className="max-w-2xl space-y-6" data-testid="billing-free">
        <div><h1 className="font-head font-black text-2xl">Billing & Usage</h1><p className="text-muted-foreground text-sm">Gameify is currently free for everyone.</p></div>
        <div className="brut p-6 flex items-center gap-3 text-neon-green" data-testid="billing-free-banner">
          <Unlock className="w-7 h-7" />
          <div>
            <p className="font-head font-bold text-lg text-foreground">Free — no charges</p>
            <p className="text-muted-foreground text-sm">Unlimited participants can join your arena at no cost. There's nothing to pay right now.</p>
          </div>
        </div>
        <div className="brut-primary bg-card p-6 rounded-2xl grid grid-cols-2 gap-4 text-sm">
          <Row l="Registered" v={usage.registered} icon={Users} />
          <Row l="Current charge" v={`${S}0`} />
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-2xl space-y-6">
      <div><h1 className="font-head font-black text-2xl">Billing & Usage</h1><p className="text-muted-foreground text-sm">Pay per event — your first {usage.free_allowance} participants are free.</p></div>

      <div className="brut-primary bg-card p-6 rounded-2xl">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Row l="Registered" v={usage.registered} icon={Users} />
          <Row l="Free allowance" v={usage.free_allowance} />
          <Row l="Additional participants" v={usage.additional} />
          <Row l="Paid blocks" v={usage.paid_blocks} />
        </div>
        <div className="border-t border-border my-4" />
        <div className="flex items-center justify-between">
          <div><p className="text-sm text-muted-foreground">Current usage charge</p><p className="font-mono-num font-extrabold text-4xl">{S}{usage.charge}</p></div>
          <div className="text-right"><p className="text-sm text-muted-foreground">Balance due</p><p className={`font-mono-num font-extrabold text-2xl ${usage.balance_due > 0 ? "text-primary" : "text-neon-green"}`}>{S}{usage.balance_due}</p></div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Next billing threshold: {usage.next_threshold} participants · {S}{usage.block_price} per {usage.block_size}</p>
      </div>

      {usage.balance_due > 0 ? (
        <div className="brut p-6 text-center">
          <Lock className="w-8 h-8 mx-auto text-primary mb-2" />
          <h2 className="font-head font-bold text-lg mb-1">Your arena has grown past its free/paid limit</h2>
          <p className="text-muted-foreground text-sm mb-4">Unlock the next block of {usage.block_size} players for {S}{usage.block_price}.</p>
          <Button className="rounded-full font-bold px-8 brut-hard" onClick={pay} disabled={paying} data-testid="pay-btn">
            {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Pay {S}{usage.balance_due} <span className="text-xs ml-1 opacity-70">(Razorpay)</span></>}
          </Button>
        </div>
      ) : (
        <div className="brut p-5 flex items-center gap-3 text-neon-green"><Unlock className="w-6 h-6" /><p className="font-semibold">You're all paid up — participants can keep joining.</p></div>
      )}

      {usage.payments.length > 0 && (
        <div className="brut p-5">
          <h2 className="font-head font-bold mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" />Payment history</h2>
          <div className="space-y-2">
            {usage.payments.map((p) => (
              <div key={p.payment_id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                <div><p className="font-mono-num">{p.invoice_no}</p><p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()} · {p.provider}</p></div>
                <span className="font-mono-num font-bold">{S}{p.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ l, v }) { return (<div><p className="text-muted-foreground">{l}</p><p className="font-mono-num font-bold text-xl">{v}</p></div>); }
